import { QuestionCircleOutlined } from '@ant-design/icons'
import { Tooltip } from 'antd'
import classNames from 'classnames'
import { useAtom } from 'jotai'
import { useEffect, useState } from 'react'
import { MyBtn } from '~/components/ui/MyBtn'
import { MyForm } from '~/components/ui/MyForm'
import { MyIcon } from '~/components/ui/MyIcon'
import { MyModal } from '~/components/ui/MyModal'
import { AiCreateArtworkModel } from '~/graphqls/types'
import { isNullOrUndefined, uniqueFunc } from '~/utils'
import styles from '../AiForm.module.css'
import { OptionsTabs } from '../OptionsTabs'
import {
  cfgAtom,
  clipSkipAtom,
  ensdAtom,
  modelCodeAtom,
  paintTimesAtom,
  paintTimesStepAtom,
  resolutionKeyAtom,
  selectModelAtom,
  selectSamplerAtom,
  selectSizeAtom,
  tagCategoryIdAtom,
  styleDecorationAtom,
  StyleDecorationInput,
} from './atom'
import useOptions from './useOptions'
import { useType } from '../useType'
import { useSetDefaultPaintParams } from './useSetDefaultPaintParams'

/** 默认显示数量 */
const DEFAULT_SHOW_COUNT = 14

export interface ModelThemeOptionsProps {
  name: string
  url: string
  isNew: boolean | null | undefined
  modelCode: number
  category: string
  model: AiCreateArtworkModel
  cfg?: number | undefined | null
  samplerIndex?: number | undefined | null
  resolutionKey?: string | undefined | null
  ensd?: number | undefined | null
  clipSkip?: number | undefined | null
  onlyMember?: boolean | undefined | null
  whiteListModel?: boolean | undefined | null
  tag?: string | null | undefined
  generateNum?: number | undefined | null
  resolutionDefaultSize?: string | undefined | null
  tagModelLinkId?: number | null | undefined
  defaultSelectStyleDecorationOption?: StyleDecorationInput[] | undefined | null
}

const widthMap = {
  large: 'calc((100% - 36px) / 4)',
  middle: 'calc((100% - 48px) / 5)',
  small: 'calc((100% - 72px) / 7)',
}

export const ModelThemeItem: React.FC<{
  item: ModelThemeOptionsProps
  onChecked: () => void
  isActive: boolean
  size?: 'large' | 'middle' | 'small'
}> = ({ item, onChecked, isActive, size = 'middle' }) => {
  const isMJ = item.model === AiCreateArtworkModel.MIDJOURNEY

  return (
    <div
      data-model-code={item.modelCode}
      style={{
        background: `url(${item.url}) no-repeat center center/cover`,
        width: widthMap[size],
      }}
      onClick={onChecked}
      className="h-16 mb-3 rounded-lg overflow-hidden cursor-pointer relative"
    >
      {!isMJ && item.whiteListModel ? (
        <img
          className="h-[14px] absolute top-1 right-1"
          src="/images/ai/whitelist_model_v2.png"
          alt=""
        />
      ) : !isMJ && item.onlyMember ? (
        <img
          className="h-[14px] absolute top-1 right-1"
          src="/images/ai/vip_model.png"
          alt=""
        />
      ) : (
        item.isNew && (
          <img
            className="h-[14px] absolute top-1 right-1"
            src="/images/ai/new.png"
            alt=""
          />
        )
      )}
      <div
        className={classNames('h-16 rounded-lg overflow-hidden', {
          '!border-neutral-800 !border-2 !border-solid': isActive,
        })}
      >
        <div
          className="w-full h-full flex items-center justify-center"
          style={{
            background:
              'linear-gradient(180deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.5) 100%)',
          }}
        >
          <div className="text-neutral-800 font-semibold select-none">
            {item.name}
          </div>
        </div>
      </div>
    </div>
  )
}

export const ModelThemeComp = () => {
  const [selectModel, setSelectModel] = useAtom(selectModelAtom)
  const [modelCode, setModelCode] = useAtom(modelCodeAtom)
  const [, setResolutionKey] = useAtom(resolutionKeyAtom)
  const [, setSelectSampler] = useAtom(selectSamplerAtom)
  const [, setCfg] = useAtom(cfgAtom)
  const [, setEnsd] = useAtom(ensdAtom)
  const [, setClipSkip] = useAtom(clipSkipAtom)
  const [, setPaintTimes] = useAtom(paintTimesAtom)
  const [, setPaintTimesStep] = useAtom(paintTimesStepAtom)
  const [, setSelectSize] = useAtom(selectSizeAtom)
  const [, setTagCategoryId] = useAtom(tagCategoryIdAtom)

  const setDefaultPaintParams = useSetDefaultPaintParams()

  const { isTransparent, is4k } = useType()

  const [visible, setVisible] = useState(false)
  const [, setCurrentIndex] = useState(0)

  const { modelThemeOptions, curModelThemeOptions } = useOptions()

  const tabs = uniqueFunc(
    modelThemeOptions
      ?.filter(item => !!item.category)
      ?.map(item => ({
        name: item.category,
        model: item.model,
      })),
    'model'
  )
  const showMore =
    curModelThemeOptions?.length > DEFAULT_SHOW_COUNT && !isTransparent

  /** 特殊的交互，和角色同人等不同，选中的不置顶
   * 透明图模型：展示所有支持的模型
   */
  const defaultOptions = isTransparent
    ? curModelThemeOptions
    : uniqueFunc(
        [
          ...curModelThemeOptions?.slice(0, DEFAULT_SHOW_COUNT),
          ...curModelThemeOptions.filter(o => o.modelCode === modelCode),
        ],
        'name'
      )
  const handleClickOption = (item: ModelThemeOptionsProps) => {
    setModelCode(item.modelCode)
    setResolutionKey(item.resolutionKey!)
    item.resolutionDefaultSize && setSelectSize(item.resolutionDefaultSize) // 备注：老板要求部分模型主题有默认尺寸比
    if (is4k && (item as any).fourKDefault) {
      setDefaultPaintParams((item as any).fourKDefault)
    } else {
      setDefaultPaintParams(item as any)
    }
  }

  /** 默认选中第一个模型主题 */
  useEffect(() => {
    const index =
      curModelThemeOptions.findIndex(item => item.modelCode === modelCode) ?? -1
    if (index === -1) {
      setModelCode(curModelThemeOptions?.[0]?.modelCode!)
      setResolutionKey(curModelThemeOptions?.[0]?.resolutionKey!)
      curModelThemeOptions?.[0]?.resolutionDefaultSize &&
        setSelectSize(curModelThemeOptions?.[0]?.resolutionDefaultSize)
      if (is4k && (curModelThemeOptions?.[0] as any).fourKDefault) {
        setDefaultPaintParams((curModelThemeOptions?.[0] as any).fourKDefault)
      } else {
        setDefaultPaintParams(curModelThemeOptions?.[0] as any)
      }
    } else {
      setPaintTimes(curModelThemeOptions?.[index]?.generateNum ?? 1)
      setPaintTimesStep(curModelThemeOptions?.[index]?.generateNum ?? 1)
      setResolutionKey(curModelThemeOptions?.[index]?.resolutionKey!)
      setTagCategoryId(curModelThemeOptions?.[index]?.tagModelLinkId)
    }
  }, [curModelThemeOptions, modelCode, is4k])

  return (
    <>
      <MyForm.Item
        label={
          <>
            <div className=" w-screen flex justify-between items-center">
              <span>
                <span className="text-neutral-500">* 模型主题 </span>
                <Tooltip
                  placement="topLeft"
                  arrowPointAtCenter
                  title="通过模型主题设定，可以设定最终生成出来的画面是什么材质和风格"
                >
                  <QuestionCircleOutlined />
                </Tooltip>
              </span>
            </div>
          </>
        }
        className={classNames(styles.borderLessFormItem, '!mb-5')}
      >
        <div className="flex flex-wrap gap-3">
          {defaultOptions.map(item => {
            const isActive = modelCode === item.modelCode
            const isLarge = selectModel === AiCreateArtworkModel.MIDJOURNEY
            return (
              <ModelThemeItem
                key={`${item.name}_${item.modelCode}`}
                isActive={isActive}
                size={isLarge ? 'large' : 'middle'}
                item={item}
                onChecked={() => handleClickOption(item)}
              />
            )
          })}
          {showMore && (
            <div
              className={classNames(
                'h-16 mb-3 rounded-lg text-neutral-800 font-semibold border-2 border-neutral-300 cursor-pointer overflow-hidden flex items-center justify-center'
              )}
              style={{
                background: 'rgba(35, 38, 47, 0.32)',
                width: 'calc((100% - 48px) / 5)',
              }}
              onClick={() => setVisible(true)}
            >
              <MyIcon
                className="text-[14px] leading-[16px] font-normal"
                icon={'more1'}
              />
              <span className="ml-0.5">更多</span>
            </div>
          )}
        </div>
      </MyForm.Item>
      <MyModal
        open={visible}
        title={<>模型主题</>}
        onCancel={() => setVisible(false)}
        maskStyle={{
          backgroundColor: 'rgba(0,0,0,0.8)',
        }}
        dark
        width={1440}
        footer={null}
        centered
        style={{ top: 0, paddingBottom: 0 }}
        isFullScreen
      >
        <OptionsTabs
          options={tabs.map(item => {
            return {
              label: item?.name,
            }
          })}
          currentIndex={tabs.findIndex(item => item.model === selectModel)}
          setCurrentIndex={setCurrentIndex}
          onChange={index => setSelectModel(tabs[index].model)}
        />
        <div
          className="flex content-start flex-wrap mb-3 gap-3 min-h-[100px] overflow-auto scrollbar"
          style={{
            height: `calc(100vh - 216px)`, // 216px为其他元素的高度+间距
          }}
        >
          {curModelThemeOptions.map(item => {
            const isActive = modelCode === item.modelCode
            const isLarge = selectModel === AiCreateArtworkModel.MIDJOURNEY
            return (
              <ModelThemeItem
                key={`${item.name}_${item.modelCode}`}
                isActive={isActive}
                size={isLarge ? 'large' : 'small'}
                item={item}
                onChecked={() => handleClickOption(item)}
              />
            )
          })}
        </div>
        <div className="flex justify-between items-center gap-3">
          <MyBtn
            block
            type="default"
            className="!bg-neutral-200 !border-neutral-200 !text-neutral-800"
            onClick={() => {
              setVisible(false)
            }}
          >
            取消
          </MyBtn>
          <MyBtn block type="primary" onClick={() => setVisible(false)}>
            确定
          </MyBtn>
        </div>
      </MyModal>
    </>
  )
}
